import React from 'react'

import { F0Box, F0TagBalance, F0Text } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import { formatDuration } from 'modules/ticketing/lib/formatDuration'

type KpiCardProps = {
  title: string
  value: number
  previous?: number | null
  isAllTime: boolean
  formatValue?: (v: number) => string
  invertStatus?: boolean
}

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  previous,
  isAllTime,
  formatValue,
  invertStatus,
}) => {
  const format = (v: number) => (formatValue ? formatValue(v) : `${v}`)

  const canCompare = !isAllTime && previous != null && previous > 0
  const percentage = canCompare ? Math.round(((value - previous) / previous) * 100) : null
  const showComparison = percentage != null && percentage !== 0

  return (
    <F0Box grow minWidth="44">
      <Widget header={{ title }}>
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
          <F0Text variant="label" content={format(value)} />
          {showComparison && previous && (
            <F0TagBalance
              percentage={percentage}
              amount={{
                numericValue: 0,
                formatter: () => format(previous),
              }}
              invertStatus={invertStatus}
            />
          )}
        </F0Box>
      </Widget>
    </F0Box>
  )
}

type InsightsKpiCardsProps = {
  assignedCount: number
  assignedCountPrevious?: number | null
  resolvedCount: number
  resolvedCountPrevious?: number | null
  inProgressCount: number
  inProgressCountPrevious?: number | null
  averageResolutionTimeSeconds?: number | null
  averageResolutionTimeSecondsPrevious?: number | null
  slaCompliancePercent?: number | null
  slaCompliancePercentPrevious?: number | null
  isAllTime: boolean
}

export const InsightsKpiCards: React.FC<InsightsKpiCardsProps> = ({
  assignedCount,
  assignedCountPrevious,
  resolvedCount,
  resolvedCountPrevious,
  inProgressCount,
  inProgressCountPrevious,
  averageResolutionTimeSeconds,
  averageResolutionTimeSecondsPrevious,
  slaCompliancePercent,
  slaCompliancePercentPrevious,
  isAllTime,
}) => {
  return (
    <F0Box display="flex" flexDirection="row" flexWrap="wrap" gap="md">
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.assigned')}
        value={assignedCount}
        previous={assignedCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.resolved')}
        value={resolvedCount}
        previous={resolvedCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.in_progress')}
        value={inProgressCount}
        previous={inProgressCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.avg_resolution_time')}
        value={averageResolutionTimeSeconds ?? 0}
        previous={averageResolutionTimeSecondsPrevious}
        isAllTime={isAllTime}
        formatValue={formatDuration}
        invertStatus
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.sla_compliance')}
        value={slaCompliancePercent ?? 0}
        previous={slaCompliancePercentPrevious}
        isAllTime={isAllTime}
        formatValue={(v) => `${v}%`}
      />
    </F0Box>
  )
}
